import React, { useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AEEventForm from "./AEEventForm";
import { useDispatch, useSelector } from "react-redux";
import {
  addEvent,
  updateEvent,
  removeEvent,
  setEvents,
} from "../../Redux/slices/addEnquirySlice";
import { emptyEvent } from "../../Redux/slices/addEnquirySlice";
import { VM_Colors } from "../../Styles/VMStyles";
import { API_GET_EVENT_DETAILS } from "../../Util/URLConstants";
import axios from "axios";
import { toast } from "react-toastify";
import {
  dateToTimeString,
  normalizeBookingForArray,
  syncSubVenueWithBookingFor,
  toDateKey,
} from "../../Util/UtilHelper";
import { ADD } from "../../Util/VMConstants";

const align_right = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
};

function AEEventTabs({ enquiry_id, arrived }) {
  const dispatch = useDispatch();
  const events = useSelector((state) => state.addEnquiry.events);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteIndex, setDeleteIndex] = useState(null);

  /* ---------------- TAB CHANGE ---------------- */
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  /* ---------------- FORM CHANGE ---------------- */
  const handleFormChange = (newData) => {
    dispatch(updateEvent({ index: activeTab, data: newData }));
  };

  /* ---------------- VALIDATION BEFORE ADD ---------------- */
  const isCurrentEventValid = () => {
    const currentEvent = events[activeTab];
    if (!currentEvent) return false;

    const { eventDetails } = currentEvent;

    if (!eventDetails?.event_type) {
      toast.error("Please select Event Type before adding another event");
      return false;
    }

    if (!eventDetails?.event_date) {
      toast.error("Please select Event Date before adding another event");
      return false;
    }

    return true;
  };

  /* ---------------- ADD EVENT ---------------- */
  const handleAddEvent = () => {
    if (!isCurrentEventValid()) return;

    dispatch(addEvent());
    setActiveTab((prev) => prev + 1);
  };

  /* ---------------- DELETE EVENT ---------------- */
  const confirmDelete = () => {
    dispatch(removeEvent({ index: deleteIndex }));
    if (activeTab >= deleteIndex) {
      setActiveTab((prev) => Math.max(prev - 1, 0));
    }
    setDeleteIndex(null);
  };

  const isEventRowEmpty = (event) => {
    if (!event || typeof event !== "object") return true;
    return ![
      event.event_date,
      event.event_type,
      event.event_name,
      event.event_subvenue_id,
      event.event_sub_venue,
      event.event_booking_for,
      event.event_no_of_guests,
      event.event_check_in,
      event.event_check_out,
      event.event_rent_charges,
    ].some(
      (value) => value !== null && value !== undefined && String(value).trim() !== ""
    );
  };

  /* ---------------- FETCH EVENT DETAILS (EDIT / VIEW) ---------------- */
  useEffect(() => {
    if (!enquiry_id) return;
    if (arrived === ADD) return; // do not fetch in ADD mode

    const fetchEnquiryEventDetails = async () => {
      try {
        const response = await axios.get(API_GET_EVENT_DETAILS, {
          params: { enquiry_id },
        });

        const data = response.data;

        if (data.status_code === 200 && Array.isArray(data.data)) {
          const fetchedEvents = data.data
            .filter((event) => !isEventRowEmpty(event))
            .map((event) => {
              const bookingFor = normalizeBookingForArray(event.event_booking_for);
              const rawSubVenue = event.event_sub_venue;
              const subVenue =
                rawSubVenue && typeof rawSubVenue === "object"
                  ? { ...rawSubVenue }
                  : {
                      sub_venue_id: event.event_subvenue_id,
                      sub_venue_name: rawSubVenue || "",
                    };

              return {
              event_id: event.id,
              eventDetails: syncSubVenueWithBookingFor({
                event_date: toDateKey(event.event_date),
                event_type: event.event_type,
                event_name: event.event_name,
                sub_venue_id: event.event_subvenue_id,
                sub_venue: subVenue,
                booking_for: bookingFor,
                no_of_guests: event.event_no_of_guests,
                check_in: dateToTimeString(event.event_check_in),
                check_out: dateToTimeString(event.event_check_out),
                rent_charges: event.event_rent_charges,
              }),
              diningDetails: Array.isArray(event.dining_details)
                ? event.dining_details
                : event.dining_details
                ? [event.dining_details]
                : [],
              accomodationDetails: {
                charge_type: event.accomodation_charge_type,
                no_of_rooms: event.accomodation_no_of_rooms,
                rooms_data: event.accomodation_rooms_data || [],
                persons_data: event.accomodation_persons_data || [],
              },
              serviceDetails: Array.isArray(event.service_details)
                ? event.service_details.map((service) => ({
                    service_id: service.service_id ?? service.id,
                    service_name: service.service_name,
                    service_charge: Number(service.service_charge) || 0,
                  }))
                : [],
            };
            });

          dispatch(
            setEvents(
              fetchedEvents.length > 0
                ? fetchedEvents
                : [structuredClone(emptyEvent)]
            )
          );
        } else {
          toast.error(data.message || "Failed to fetch event details");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error fetching event details");
      }
    };

    fetchEnquiryEventDetails();
  }, [enquiry_id, arrived, dispatch]);

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ width: "100%" }}>
      {/* Tabs Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="standard"
            TabIndicatorProps={{ style: { height: 3 } }}
            sx={{
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                flexWrap: "wrap",
                gap: 1,
              },
            }}
          >
            {events.map((_, index) => {
              const isSelected = activeTab === index;

              return (
                <Tab
                  key={index}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                       color: isSelected
                          ? VM_Colors.white
                          : VM_Colors.black,
                      }}
                    >
                      <span>{`Enter Event ${index + 1} Details`}</span>

                      {events.length > 1 && (
                        <DeleteIcon
                          fontSize="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteIndex(index);
                          }}
                          sx={{
                            cursor: "pointer",
                            color: isSelected
                              ? VM_Colors.white
                              : VM_Colors.black,
                            "&:hover": { color: "red" },
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    borderRadius: 1,
                    minWidth: "auto",
                    px: 1.5,
                    color: isSelected
                      ? VM_Colors.white
                      : VM_Colors.black,
                    backgroundColor: isSelected
                      ? VM_Colors.primary
                      : VM_Colors.white1,
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Add Event Button */}
        <Box sx={align_right}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddEvent}
            variant="contained"
            size="small"
            sx={{ px: 4 ,pt:1,pb:1}}
          >
            Add Other Event
          </Button>
        </Box>
      </Box>

      <Divider sx={{ borderColor: VM_Colors.dark_gradient, mb: 2 }} />

      {/* Tab Content */}
      <AEEventForm
        tabIndex={activeTab}
        arrived={arrived}
        onChange={handleFormChange} // ✅ controlled form
      />

      {/* Delete Confirmation */}
      <Dialog
        open={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
      >
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this event?
          <br />
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIndex(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AEEventTabs;
